var _class, _descriptor, _token, _relatedToken;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { dependentKeyCompat } from '@ember/object/compat';
import { tracked } from '@glimmer/tracking';
import { cached } from "ember-cached-decorator-polyfill";
import { resolve } from 'rsvp';
import { unsubscribe } from '../record-notification-manager';
import { internalModelFactoryFor, recordIdentifierFor } from '../store/internal-model-factory';
import Reference from './reference';

/**
  @module @ember-data/store
*/

/**
 A `BelongsToReference` is a low-level API that allows users and
 addon authors to perform meta-operations on a belongs-to
 relationship.

 @class BelongsToReference
 @public
 @extends Reference
 */
let BelongsToReference = (_class = (_token = /*#__PURE__*/_classPrivateFieldLooseKey("token"), _relatedToken = /*#__PURE__*/_classPrivateFieldLooseKey("relatedToken"), class BelongsToReference extends Reference {
  constructor(store, parentIdentifier, belongsToRelationship, key) {
    super(store, parentIdentifier);
    // unsubscribe tokens given to us by the notification manager
    Object.defineProperty(this, _token, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _relatedToken, {
      writable: true,
      value: null
    });
    _initializerDefineProperty(this, "_ref", _descriptor, this);
    this.key = key;
    this.belongsToRelationship = belongsToRelationship;
    this.type = belongsToRelationship.definition.type;
    const parent = internalModelFactoryFor(store).peek(parentIdentifier);
    this.parent = parent.recordReference;
    this.parentIdentifier = parentIdentifier;
    _classPrivateFieldLooseBase(this, _token)[_token] = store._notificationManager.subscribe(parentIdentifier, (_, bucket, notifiedKey) => {
      if ((bucket === 'relationships' || bucket === 'property') && notifiedKey === key) {
        this._ref++;
      }
    });

    // TODO inverse
  }
  destroy() {
    unsubscribe(_classPrivateFieldLooseBase(this, _token)[_token]);
    if (_classPrivateFieldLooseBase(this, _relatedToken)[_relatedToken]) {
      unsubscribe(_classPrivateFieldLooseBase(this, _relatedToken)[_relatedToken]);
    }
  }
  get _relatedIdentifier() {
    this._ref; // consume the tracked prop
    if (_classPrivateFieldLooseBase(this, _relatedToken)[_relatedToken]) {
      unsubscribe(_classPrivateFieldLooseBase(this, _relatedToken)[_relatedToken]);
    }
    let resource = this._resource();
    if (resource && resource.data) {
      const identifier = this.store.identifierCache.getOrCreateRecordIdentifier(resource.data);
      _classPrivateFieldLooseBase(this, _relatedToken)[_relatedToken] = this.store._notificationManager.subscribe(identifier, (_, bucket, notifiedKey) => {
        if (bucket === 'identity' || (bucket === 'attributes' || bucket === 'property') && notifiedKey === 'id') {
          this._ref++;
        }
      });
      return identifier;
    }
    return null;
  }

  /**
   The `id` of the record that this reference refers to. Together, the
   `type()` and `id()` methods form a composite key for the identity
   map. This can be used to access the id of an async relationship
   without triggering a fetch that would normally happen if you
   attempted to use `record.get('relationship.id')`.
    Example
    ```javascript
   // models/blog.js
   import Model, { belongsTo } from '@ember-data/model';
    export default class BlogModel extends Model {
    @belongsTo({ async: true }) user;
   }
    let blog = store.push({
      data: {
        type: 'blog',
        id: 1,
        relationships: {
          user: {
            data: { type: 'user', id: 1 }
          }
        }
      }
    });
   let userRef = blog.belongsTo('user');
    // get the identifier of the reference
   if (userRef.remoteType() === "id") {
      let id = userRef.id();
    }
   ```
    @method id
    @public
   @return {String} The id of the record in this belongsTo relationship.
   */
  id() {
    return this._relatedIdentifier?.id || null;
  }
  _resource() {
    return this.recordData.getBelongsTo(this.key);
  }

  /**
   `push` can be used to update the data in the relationship and Ember
   Data will treat the new data as the canonical value of this
   relationship on the backend.
    Example
    ```app/models/blog.js
   import Model, { belongsTo } from '@ember-data/model';
    export default class BlogModel extends Model {
      @belongsTo({ async: true }) user;
    }
    let blog = store.push({
      data: {
        type: 'blog',
        id: 1,
        relationships: {
          user: {
            data: { type: 'user', id: 1 }
          }
        }
      }
    });
   let userRef = blog.belongsTo('user');
    // provide data for reference
   userRef.push({
      data: {
        type: 'user',
        id: 1,
        attributes: {
          username: "@user"
        }
      }
    }).then(function(user) {
      userRef.value() === user;
    });
   ```
    @method push
    @public
   @param {Object|Promise} objectOrPromise a promise that resolves to a JSONAPI document object describing the new value of this relationship.
   @return {Promise<record>} A promise that resolves with the new value in this belongs-to relationship.
   */
  async push(data) {
    const jsonApiDoc = await resolve(data);
    let record = this.store.push(jsonApiDoc);
    const {
      graph,
      identifier
    } = this.belongsToRelationship;
    this.store._backburner.join(() => {
      graph.push({
        op: 'replaceRelatedRecord',
        record: identifier,
        field: this.key,
        value: recordIdentifierFor(record)
      });
    });
    return record;
  }

  /**
   `value()` synchronously returns the current value of the belongs-to
   relationship. Unlike `record.get('relationshipName')`, calling
   `value()` on a reference does not trigger a fetch if the async
   relationship is not yet loaded. If the relationship is not loaded
   it will always return `null`.
    Example
    ```javascript
   // models/blog.js
   import Model, { belongsTo } from '@ember-data/model';
    export default class BlogModel extends Model {
     @belongsTo({ async: true }) user;
   }
    let blog = store.push({
      data: {
        type: 'blog',
        id: 1,
        relationships: {
          user: {
            data: { type: 'user', id: 1 }
          }
        }
      }
    });
   let userRef = blog.belongsTo('user');
    userRef.value(); // null
    // provide data for reference
   userRef.push({
      data: {
        type: 'user',
        id: 1,
        attributes: {
          username: "@user"
        }
      }
    }).then(function(user) {
      userRef.value(); // user
    });
   ```
    @method value
    @public
   @return {Model} the record in this relationship
   */
  value() {
    let resource = this._resource();
    if (resource && resource.data) {
      let inverseInternalModel = this.store._internalModelForResource(resource.data);
      if (inverseInternalModel && inverseInternalModel.currentState.isLoaded) {
        return inverseInternalModel.getRecord();
      }
    }
    return null;
  }

  /**
   Loads a record in a belongs-to relationship if it is not already
   loaded. If the relationship is already loaded this method does not
   trigger a new load.
    Example
    ```javascript
   // models/blog.js
   import Model, { belongsTo } from '@ember-data/model';
    export default class BlogModel extends Model {
     @belongsTo({ async: true }) user;
   }
    let blog = store.push({
      data: {
        type: 'blog',
        id: 1,
        relationships: {
          user: {
            data: { type: 'user', id: 1 }
          }
        }
      }
    });
   let userRef = blog.belongsTo('user');
    userRef.value(); // null
    userRef.load().then(function(user) {
      userRef.value() === user
    });
   ```
    You may also pass in an options object whose properties will be
   fed forward. This enables you to pass `adapterOptions` into the
   request given to the adapter via the reference.
    Example
    ```javascript
   userRef.load({ adapterOptions: { isPrivate: true } }).then(function(user) {
     userRef.value() === user;
   });
   ```
   ```app/adapters/user.js
   import Adapter from '@ember-data/adapter';
    export default class UserAdapter extends Adapter {
     findRecord(store, type, id, snapshot) {
       // In the adapter you will have access to adapterOptions.
       let adapterOptions = snapshot.adapterOptions;
     }
   });
   ```
    @method load
    @public
   @param {Object} options the options to pass in.
   @return {Promise} a promise that resolves with the record in this belongs-to relationship.
   */
  load(options) {
    let parentInternalModel = internalModelFactoryFor(this.store).peek(this.parentIdentifier);
    return parentInternalModel.getBelongsTo(this.key, options);
  }

  /**
   Triggers a reload of the value in this relationship. If the
   remoteType is `"link"` Ember Data will use the relationship link to
   reload the relationship. Otherwise it will reload the record by its
   id.
    Example
    ```javascript
   // models/blog.js
   import Model, { belongsTo } from '@ember-data/model';
    export default class BlogModel extends Model {
     @belongsTo({ async: true }) user;
   }
    let blog = store.push({
      data: {
        type: 'blog',
        id: 1,
        relationships: {
          user: {
            data: { type: 'user', id: 1 }
          }
        }
      }
    });
   let userRef = blog.belongsTo('user');
    userRef.reload().then(function(user) {
      userRef.value() === user
    });
   ```
    You may also pass in an options object whose properties will be
   fed forward. This enables you to pass `adapterOptions` into the
   request given to the adapter via the reference. A full example
   can be found in the `load` method.
    Example
    ```javascript
   userRef.reload({ adapterOptions: { isPrivate: true } })
   ```
    @method reload
    @public
   @param {Object} options the options to pass in.
   @return {Promise} a promise that resolves with the record in this belongs-to relationship after the reload has completed.
   */
  reload(options) {
    let parentInternalModel = internalModelFactoryFor(this.store).peek(this.parentIdentifier);
    return parentInternalModel.reloadBelongsTo(this.key, options).then(internalModel => {
      return this.value();
    });
  }
}), _descriptor = _applyDecoratedDescriptor(_class.prototype, "_ref", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "_relatedIdentifier", [cached, dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "_relatedIdentifier"), _class.prototype), _class);
export { BelongsToReference as default };